import React, { useEffect, useState, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import moment from 'moment'
import cn from 'classnames'

import styles from './styles.module.scss'

import { showDocumentForm } from '../../../store/documents/actions'
import { getDocuments, sendDocumentReminder, removeDocuments, getDocumentTypes, getCompanyEmployeesAll, getPlaces } from '../../../api'
import useNotify from '../../../hooks/useNotify'
import getUniqueArrayByKey from '../../../helpers/getUniqueArrayByKey'
import { generateResourcesFromEmployees, getCheckedEmployeeIds } from '../../TimeOff/Calendar/utils'
import usePermissions from '../../Core/usePermissions'

import { PlusIcon } from '../Icons' 
import Table from '../../Core/Table'
import Checkbox from '../../Core/Checkbox/Checkbox2'
import SearchIcon from '../../Icons/SearchIcon'
import Input from '../../Core/Input/Input'
import DocumentMenuActions from '../DocumentMenuActions'
import ConfirmationModal from '../../Core/Dialog/ConfirmationModal'
import CustomSelect from '../../Core/CustomSelect'

const PER_PAGE = 20

const columnHelper = createColumnHelper()

const getInitials = (name, surname) => {
  return `${(name || '').charAt(0)}${(surname || '').charAt(0)}`.toUpperCase()
}

const getExpirationColor = (label) => {
  if (!label) return '#808F94'
  if (label.toLowerCase().startsWith('expired')) return '#DE4343'
  if (label.toLowerCase().startsWith('expires')) return '#D77000'
  return '#808F94'
}

const getStatusColors = (status) => {
  const value = (status || '').toLowerCase()
  if (value.includes('valid') || value.includes('acknowledged') || value.includes('approved') || value.includes('signed')) {
    return { background: '#D1F9CB', text: '#32BF1F', border: '#BEF3B7' }
  }
  if (value.includes('pending') || value.includes('review')) {
    return { background: '#FFD4A5', text: '#D77000', border: '#E7C88C' }
  }
  if (value.includes('declined') || value.includes('overdue') || value.includes('expired') || value.includes('replacement')) {
    return { background: '#FFBDBD', text: '#D70000', border: '#F77C7C' }
  }
  return { background: '#E9E9E9', text: '#919191', border: '#C6C6C6' }
}

const getActionCellContent = (t, { required, done, doneAt }) => {
  if (!required) {
    return { label: '-', color: '#808F94', sub: null }
  }
  if (done) {
    return { label: t('Done'), color: '#32BF1F', sub: doneAt ? moment(doneAt).format('MMM D, HH:mm') : null }
  }
  return { label: t('Pending'), color: '#D77000', sub: null }
}

const getDocumentRowId = (document) => {
  const assignmentId = document.employee_id != null
    ? document.employee_id
    : document.assigned_to?.place_id || document.assigned_to?.type || 'document'
  return `${document.document_id}_${assignmentId}`
}

const renderProgressCell = (t, summary) => {
  if (!summary) return <span className={styles.mutedText}>-</span>
  return (
    <div className={styles.progressCell}>
      <div className={styles.progressSummary}>
        <span>{summary.completed || 0}</span>
        <span>/</span>
        <span>{summary.total || 0}</span>
        <span>{summary.percentage || 0}%</span>
      </div>
      <div className={styles.progressTrack}>
        <div className={styles.progressBar} style={{ width: `${summary.percentage || 0}%` }} />
      </div>
      <div className={styles.actionSub}>
        {t('{{count}} pending', { count: summary.pending ||  summary.total - summary.completed})}
      </div>
    </div>
  )
}

 moment.locale(localStorage.getItem('i18nextLng') || 'en')

const Documents = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { id: companyId } = useParams()
  const notify = useNotify()
  const history = useHistory()
  const permissions = usePermissions([{
    name: 'can_upload_documents',
    permission: 'documents_can_upload_documents'
  }])

  const [{
    current_page, data, total, last_page, unique_employee_count, unique_place_count,
    ...counts
  }, setDocuments] = useState({
    current_page: 1,
    data: [],
    total: 0,
    last_page: 1,
    unique_employee_count: 0,
    unique_place_count: 0,
    expiring_soon_count: 0,
    awaiting_acknowledgment_count: 0,
    awaiting_approval_count: 0,
    awaiting_signature_count: 0,
    awaiting_review_count: 0,
    awaiting_upload_count: 0,
  })
  const [loading, setLoading] = useState(false)
  const [rowSelection, setRowSelection] = useState({})
  const [selectedStatus, setSelectedStatus] = useState('')
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({
    documentTypes: [],
    employees: [],
    places: [],
  })

  const searchTimeout = useRef(null)
  const confirmationModalRef = useRef(null)

  const employeeIds = getCheckedEmployeeIds(filters.employees)
  const placeIds = getCheckedEmployeeIds(filters.places)
  const documentTypeIds = getCheckedEmployeeIds(filters.documentTypes)

  const statuses = [
    {key: '', title: t('All documents')},
    {key: 'need_attention', title: t('Needs attention')},
    {key: 'unread', title: t('Unread')},
    {key: 'expired', title: t('Expired')},
    {key: 'archived', title: t('Archived')},

    {key: 'expiring_soon', title: t('Expiring soon'), top: true, danger: true},
    {key: 'awaiting_acknowledgment', title: t('Awaiting acknowledgment'), top: true},
    {key: 'awaiting_signature', title: t('Awaiting signature'), top: true},
    {key: 'awaiting_upload', title: t('Awaiting upload'), top: true},
    {key: 'awaiting_review', title: t('Uploads to review'), top: true},
  ]

  useEffect(() => {
    const handleDocumentsUpdated = () => {
      fetchDocuments(1)
    }
    window.addEventListener('documents-updated', handleDocumentsUpdated)
    return () => {
      window.removeEventListener('documents-updated', handleDocumentsUpdated)
    }
  }, [selectedStatus])

  useEffect(() => {
    init(companyId)
  }, [companyId])

  useEffect(() => {
    if (companyId) {
      fetchDocuments(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, selectedStatus, employeeIds.join(''), placeIds.join(''), documentTypeIds.join('')])

  const init = async (companyId) => {
    const [documentTypesRes, employeesRes, placesRes] = await Promise.all([
      getDocumentTypes(companyId),
      getCompanyEmployeesAll(companyId),
      getPlaces(companyId),
    ])
    setFilters({
      documentTypes: documentTypesRes?.success ? documentTypesRes.data.map(dt => ({id: dt.id, title: dt.name, checked: false, isEmployee: true})) : [],
      employees: employeesRes?.success ? generateResourcesFromEmployees(employeesRes?.users) : [],
      places: Array.isArray(placesRes) ? placesRes.map(p => ({id: p.id, title: p.name, checked: false, isEmployee: true})) : []
    })
  }

  const fetchDocuments = async (page, search = '') => {
    setLoading(true)
    const res = await getDocuments(companyId, {
      page: page,
      per_page: PER_PAGE,
      status: selectedStatus,
      search: search,
      employee_ids: employeeIds,
      place_ids: placeIds,
      document_type_ids: documentTypeIds
    })
    if (res) {
      setDocuments(res)
    }
    setLoading(false)
  }

  const handleCreateDocument = () => {
    dispatch(showDocumentForm())
  }

  const handleUpdateDocument = documentId => {
    dispatch(showDocumentForm({documentId}))
  }

  const handlePrevPage = () => {
    if (current_page > 1) {
      fetchDocuments(current_page - 1)
    }
  }

  const handleNextPage = () => {
    if (current_page < last_page) {
      fetchDocuments(current_page + 1)
    }
  }

  const handleClickDocument = (document) => {
    history.push({search: `?tab=documents&document_id=${document.document_id}`})
    // dispatch(showDocumentPreview({documentId: document.document_id, employeeId: document.employee_id}))
  }

  const handleChangeSearch = ({target: {value}}) => {
    setSearch(value)
    searchTimeout.current && clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      fetchDocuments(1, value)
    }, 300)
  }

  const handleSendReminders = async (selectedDocuments) => {
    const data = selectedDocuments
      .filter(doc => doc.employee_id != null)
      .map(doc => ({document_id: doc.document_id, employee_id: doc.employee_id}))
    if (!data.length) return
    const res = await sendDocumentReminder(companyId, data)
    if (res?.data?.sent?.length) {
      notify.show(t('Reminder sent successfully'))
    }
  }

  const handleRemoveDocuments = async (selectedDocuments) => {
    const documentsToDelete = getUniqueArrayByKey(selectedDocuments, 'document_id')
    const result = await confirmationModalRef.current.show({
      title: t('Confirm Deletion'),
      description: t(`Are you sure you want to delete selected documents?`)
    })
    if (result.success) {
      const res = await removeDocuments(companyId, {document_ids: documentsToDelete.map(doc => doc.document_id)})
      if (res) {
        notify.show(t('Documents successfully deleted'))
        setRowSelection({})
        fetchDocuments(1)
      }
    }
  }

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'select',
      enableSorting: false,
      header: ({ table }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        </div>
      ),
      cell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        </div>
      ),
    }),
    columnHelper.display({
      id: 'document',
      header: t('Document'),
      enableSorting: false,
      cell: ({ row }) => (
        <div className={styles.documentCell}>
          <div className={styles.documentName}>{ row.original.document_name }</div>
          <div className={styles.documentSubtitle}>
            {row.original.document_type?.name || row.original.document_type_name}
          </div>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'assigned',
      header: t('Assigned to'),
      enableSorting: false,
      cell: ({ row }) => {
        const assignedTo = row.original.assigned_to
        if (assignedTo) {
          return (
            <div className={styles.assignedCell}>
              <div className={styles.initials}>{getInitials(assignedTo.label, '')}</div>
              <div className={styles.assignedInfo}>
                <div className={styles.assignedName}>
                  {assignedTo.label || t(assignedTo.type)}
                </div>
                <div className={styles.assignedSub}>
                  {assignedTo.employee_count
                    ? t('{{count}} employees', { count: assignedTo.employee_count })
                    : null}
                  &nbsp;
                  {row.original.due_date_for_action
                    ? t('Due {{date}}', {
                        date: moment(row.original.due_date_for_action).format('MMM D, YYYY'),
                      })
                    : null}
                </div>
              </div>
            </div>
          )
        }

        return (
          <div className={styles.assignedCell}>
            {row.original.employee_avatar
              ? <div className={styles.avatar}>
                  <img
                    src={`${window.location.origin}/storage/${row.original.employee_avatar}`}
                    alt={row.original.employee_full_name}
                  />
                </div>
              : <div className={styles.initials}>
                  {getInitials(row.original.employee_name, row.original.employee_surname)}
                </div>}
            <div className={styles.assignedName}>{row.original.employee_full_name}</div>
          </div>
        )
      },
    }),
    columnHelper.display({
      id: 'status',
      header: t('Status'),
      enableSorting: false,
      cell: ({ row }) => {
        const items = row.original.status?.items
        const statusItems = Array.isArray(items) && items.length
          ? items
          : [{ value: row.original.status.value, label: row.original.status.label, count: null }]
        return (
          <div className={styles.statusList}>
            {statusItems.map(status => {
              const colors = getStatusColors(status.value)
              const label = status.count
                ? `${status.count} ${t(status.label)}`
                : t(status.label)
              return (
                <div
                  key={`${status.value}-${status.count}`}
                  className={styles.statusBadge}
                  style={{
                    backgroundColor: colors.background,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <div className={styles.statusDot} style={{ backgroundColor: colors.text }} />
                  {label || '-'}
                </div>
              )
            })}
          </div>
        )
      },
    }),
    columnHelper.display({
      id: 'expiration',
      header: t('Expiration'),
      enableSorting: false,
      cell: ({ row }) => {
        const expiration = row.original.expiration || {}
        const label = expiration.label || row.original.expiration_label
        const date = expiration.display_date || expiration.date || row.original.expiration_date
        if (!label) {
          return <span className={styles.mutedText}>-</span>
        }
        return (
          <div className={styles.expirationCell}>
            <div style={{ color: getExpirationColor(label) }}>{ label }</div>
            {
              date ? (
                <div className={styles.expirationDate}>
                  {expiration.display_date || moment(date).format('MMM D, YYYY')}
                </div>
              ) : null
            }
          </div>
        )
      },
    }),
    columnHelper.display({
      id: 'uploaded',
      header: t('Uploaded'),
      enableSorting: false,
      cell: ({ row }) => {
        const uploaded = row.original.uploaded
        if (uploaded && typeof uploaded === 'object' && 'total' in uploaded) {
          return renderProgressCell(t, uploaded)
        }
        return '-'
      },
    }),
    columnHelper.display({
      id: 'seen',
      header: t('Seen'),
      enableSorting: false,
      cell: ({ row }) => {
        const seen = row.original.seen
        if (seen && typeof seen === 'object' && 'total' in seen) {
          return renderProgressCell(t, seen)
        }
        return '-'
      },
    }),
    columnHelper.display({
      id: 'acknowledged',
      header: t('Acknowledged'),
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.acknowledged && typeof row.original.acknowledged === 'object') {
          return renderProgressCell(t, row.original.acknowledged)
        }
        return '-'
      },
    }),
    columnHelper.display({
      id: 'signature',
      header: t('Signature'),
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.signature && typeof row.original.signature === 'object') {
          return renderProgressCell(t, row.original.signature)
        }
        const content = getActionCellContent(t, {
          required: row.original.require_signature,
          done: row.original.signed,
          doneAt: row.original.signed_at,
        })
        return (
          <div className={styles.actionCell}>
            <div style={{ color: content.color }}>{ content.label }</div>
            { content.sub ? <div className={styles.actionSub}>{ content.sub }</div> : null }
          </div>
        )
      },
    }),
    // columnHelper.display({
    //   id: 'updated',
    //   header: t('Updated'),
    //   enableSorting: false,
    //   cell: ({ row }) => (
    //     <span className={styles.mutedText}>
    //       {formatUpdatedAt(row.original.updated_at || row.original.document_updated_at)}
    //     </span>
    //   ),
    // }),
  ], [t])

  const table = useReactTable({
    data,
    columns,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    getRowId: getDocumentRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <div className={styles.headerTitle}>{t('Documents')}</div>
          <div className={styles.headerDesc}>{t('{{documentsCount}} documents · {{employeesCount}} employees · {{placesCount}} places', { documentsCount: total, employeesCount: unique_employee_count, placesCount: unique_place_count })}</div>
        </div>
        {
          permissions.can_upload_documents
            ? <button type="button" className={styles.button} onClick={handleCreateDocument}>
                <PlusIcon />
                { t('Upload document') }
              </button>
            : null
        }
      </div>
      <div className={styles.content}>
        <div>
          <div className={styles.topFilters} role='tablist' aria-label={t('Document status')}>
            {
              statuses.filter(f => f.top).map((filter) => (
                <button
                  key={filter.key}
                  type='button'
                  role='tab'
                  aria-selected={selectedStatus === filter.key}
                  className={cn(styles.topFilter, {
                    [styles.topFilterActive]: selectedStatus === filter.key,
                    [styles.topFilterDanger]: filter.danger,
                  })}
                  onClick={() => setSelectedStatus(prev => prev === filter.key ? '' : filter.key)}
                >
                  <span className={styles.topFilterTitle}>{filter.title}</span>
                  <span className={styles.topFilterCount}>{counts[`${filter.key}_count`]}</span>
                </button>
              ))
            }
          </div>
          <div className={styles.searchWrapper}>
            <Input
              icon={<SearchIcon />}
              placeholder={`${t('Search')}...`}
              width='100%'
              height='36px'
              value={search}
              onChange={handleChangeSearch}
            />
            <div className={styles.filterItem}>
              <CustomSelect
                placeholder={t('All document types')}
                items={filters.documentTypes}
                onChange={data => {
                  setFilters(prev => ({...prev, documentTypes: data}))
                }}
                width='100%' />
            </div>
            <div className={styles.filterItem}>
              <CustomSelect
                placeholder={t('All employees')}
                items={filters.employees}
                onChange={data => {
                  setFilters(prev => ({...prev, employees: data}))
                }}/>
            </div>
            <div className={styles.filterItem}>
              <CustomSelect
                placeholder={t('All places')}
                items={filters.places}
                className={styles.filterItemSelect}
                onChange={data => {
                  setFilters(prev => ({...prev, places: data}))
                }} />
            </div>
          </div>
          <div className={styles.filters} role='tablist' aria-label={t('Acknowledgment status')}>
            {statuses.filter(f => !f.top).map((filter) => (
              <button
                key={filter.key}
                type='button'
                role='tab'
                aria-selected={selectedStatus === filter.key}
                className={`${styles.filter} ${selectedStatus === filter.key ? styles.filterActive : ''}`}
                onClick={() => setSelectedStatus(filter.key)}
              >
                {filter.title}
                {' '}
                <span>{filter.count}</span>
              </button>
            ))}
          </div>
        </div>
        <Table
          table={table}
          columns={columns}
          loading={loading}
          onClickRow={handleClickDocument}
          placeholder={t('No documents found')}
        />
        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            { t('Showing {{count}} of {{total}} documents', { count: data.length, total }) }
          </div>
          <div className={styles.footerActions}>
            <button type="button" className={cn(styles.pageButton, { [styles.pageButtonDisabled]: current_page <= 1 })} disabled={current_page <= 1} onClick={handlePrevPage}>
              { t('Previous') }
            </button>
            <button type="button" className={cn(styles.pageButton, { [styles.pageButtonDisabled]: current_page >= last_page })} disabled={current_page >= last_page} onClick={handleNextPage}>
              { t('Next') }
            </button>
          </div>
        </div>
      </div>
      <DocumentMenuActions
        onClose={() => setRowSelection({})}
        onUpdate={handleUpdateDocument}
        onRemove={handleRemoveDocuments}
        onSendReminders={handleSendReminders}
        selectedDocuments={data.filter(doc => rowSelection[getDocumentRowId(doc)])}
        open={Object.keys(rowSelection).length} />
      <ConfirmationModal ref={confirmationModalRef} />
    </div>
  )
}

export default Documents

